export type ChatStreamHandlers = {
  onOpen?: () => void;
  onClose?: () => void;
  onToken: (delta: string) => void;
  onDone: () => void;
  onError: (message: string) => void;
};

export type ChatStreamConnection = {
  sendUserMessage: (text: string) => void;
  sendPageChange: (newPageUrl: string, prevPageUrl?: string) => void;
  close: () => void;
  getReadyState: () => number;
};

function pickDelta(o: Record<string, unknown>): string | null {
  const keys = ["delta", "content", "text", "token", "chunk", "value"] as const;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

function handleIncomingJson(raw: unknown, h: ChatStreamHandlers): void {
  if (raw == null) return;
  if (typeof raw === "string") {
    if (raw.length) h.onToken(raw);
    return;
  }
  if (typeof raw !== "object") return;
  const o = raw as Record<string, unknown>;
  const type = typeof o.type === "string" ? o.type.toLowerCase() : "";

  if (type === "error" || o.error) {
    const msg =
      (typeof o.message === "string" && o.message) ||
      (typeof o.error === "string" && o.error) ||
      "Chat error";
    h.onError(msg);
    return;
  }

  if (
    type === "done" ||
    type === "complete" ||
    type === "finished" ||
    o.done === true ||
    o.finished === true
  ) {
    h.onDone();
    return;
  }

  if (type === "token" || type === "chunk" || type === "delta" || type === "message") {
    const delta = pickDelta(o);
    if (delta) h.onToken(delta);
    return;
  }

  if (o.event === "token" || o.event === "chunk") {
    const delta = pickDelta(o);
    if (delta) h.onToken(delta);
    return;
  }

  const delta = pickDelta(o);
  if (delta) h.onToken(delta);
}

export function connectChatStream(wsUrl: string, handlers: ChatStreamHandlers): ChatStreamConnection {
  const ws = new WebSocket(wsUrl);

  ws.onopen = () => {
    handlers.onOpen?.();
  };

  ws.onmessage = (ev) => {
    const data = ev.data;
    if (typeof data === "string") {
      const t = data.trim();
      if (!t) return;
      if (t.startsWith("{") || t.startsWith("[")) {
        try {
          handleIncomingJson(JSON.parse(t) as unknown, handlers);
        } catch {
          handlers.onToken(t);
        }
      } else {
        handlers.onToken(t);
      }
    }
  };

  ws.onerror = () => {
    handlers.onError("WebSocket connection error");
  };

  ws.onclose = () => {
    handlers.onClose?.();
  };

  return {
    sendUserMessage(text: string) {
      const trimmed = text.trim();
      if (!trimmed || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "message", content: trimmed }));
    },
    sendPageChange(newPageUrl: string, prevPageUrl?: string) {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "page_change", newPageUrl, prevPageUrl }));
    },
    close() {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    },
    getReadyState() {
      return ws.readyState;
    },
  };
}
